import axiosInstance from '@/lib/services/axiosInstance';

import {
  BrandStrategy,
  Competitor,
  CompetitorResponse,
  CreatePillar,
  CreateProduct,
  CreateToneOfVoice,
  CustomerPersona,
  CustomerPersonaResponse,
  GetBrandStrategyFormResponse,
  GetBrandStrategyResponse,
  Pillar,
  PillarResponse,
  Product,
  ProductResponse,
  ToneOfVoice,
  ToneOfVoiceResponse,
  UpdateBrandStrategyResponse,
} from './types';

export const getBrandStrategy = (
  projectId: string,
  fields?: string
): Promise<GetBrandStrategyResponse> =>
  axiosInstance.get(`/projects/${projectId}/brand-strategy`, {
    params: { fields },
  });

export const getBrandStrategyForm = (
  projectId: string,
  fields?: string[]
): Promise<GetBrandStrategyFormResponse> =>
  axiosInstance.get(`/projects/${projectId}/brand-strategy/form`, {
    params: { fields },
  });

export const updateBrandStrategy = (
  projectId: string,
  brandStrategyId: string,
  data: BrandStrategy
): Promise<UpdateBrandStrategyResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}`,
    data
  );

// Customer Personas
export const addCustomerPersona = (
  projectId: string,
  brandStrategyId: string,
  data: CustomerPersona
): Promise<CustomerPersonaResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/customer-personas`,
    data
  );

export const updateCustomerPersona = (
  projectId: string,
  brandStrategyId: string,
  customerPersonaId: string,
  data: CustomerPersona
): Promise<CustomerPersonaResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/customer-personas/${customerPersonaId}`,
    data
  );

export const deleteCustomerPersona = (
  projectId: string,
  brandStrategyId: string,
  customerPersonaId: string
): Promise<void> =>
  axiosInstance.delete(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/customer-personas/${customerPersonaId}`
  );

// Pillars
export const addPillar = (
  projectId: string,
  brandStrategyId: string,
  data: CreatePillar
): Promise<PillarResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/pillars`,
    data
  );

export const updatePillar = (
  projectId: string,
  brandStrategyId: string,
  pillarId: string,
  data: Pillar
): Promise<PillarResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/pillars/${pillarId}`,
    data
  );

export const deletePillar = (
  projectId: string,
  brandStrategyId: string,
  pillarId: string
): Promise<void> =>
  axiosInstance.delete(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/pillars/${pillarId}`
  );

// Competitors
export const addCompetitor = (
  projectId: string,
  brandStrategyId: string,
  data: Competitor
): Promise<CompetitorResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/competitors`,
    data
  );

export const updateCompetitor = (
  projectId: string,
  brandStrategyId: string,
  competitorId: string,
  data: Competitor
): Promise<CompetitorResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/competitors/${competitorId}`,
    data
  );

export const deleteCompetitor = (
  projectId: string,
  brandStrategyId: string,
  competitorId: string
): Promise<void> =>
  axiosInstance.delete(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/competitors/${competitorId}`
  );

// Products
export const addProduct = (
  projectId: string,
  brandStrategyId: string,
  data: CreateProduct
): Promise<ProductResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/products`,
    data
  );

export const updateProduct = (
  projectId: string,
  brandStrategyId: string,
  productId: string,
  data: Product
): Promise<ProductResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/products/${productId}`,
    data
  );

export const deleteProduct = (
  projectId: string,
  brandStrategyId: string,
  productId: string
): Promise<void> =>
  axiosInstance.delete(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/products/${productId}`
  );

// Tone of Voices
export const addToneOfVoice = (
  projectId: string,
  brandStrategyId: string,
  data: CreateToneOfVoice
): Promise<ToneOfVoiceResponse> =>
  axiosInstance.post(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/tone-of-voices`,
    data
  );

export const updateToneOfVoice = (
  projectId: string,
  brandStrategyId: string,
  toneOfVoiceId: string,
  data: ToneOfVoice
): Promise<ToneOfVoiceResponse> =>
  axiosInstance.put(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/tone-of-voices/${toneOfVoiceId}`,
    data
  );

export const deleteToneOfVoice = (
  projectId: string,
  brandStrategyId: string,
  toneOfVoiceId: string
): Promise<void> =>
  axiosInstance.delete(
    `/projects/${projectId}/brand-strategy/${brandStrategyId}/tone-of-voices/${toneOfVoiceId}`
  );

export interface FieldOption {
  key: string;
  display: string;
}

export interface FieldOptionsResponse {
  [key: string]: FieldOption[];
}

export const getFieldOptions = (
  projectId: string
): Promise<{ data: FieldOptionsResponse }> =>
  axiosInstance.get(`/projects/${projectId}/brand-strategy/field-options`);
